import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText,
  IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, DeleteOutlined, EditOutlined, FileDownloadOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type {
  PublicHoliday, PublicHolidayCreate, PublicHolidayImportCountry,
  PublicHolidayImportSubdivision, PublicHolidaySuggestion,
} from '../../../api/attendance';
import type { LocationNode } from '../../../api/organization';
import { ConfirmDialog } from '../../../components/primitives';
import { RecalcNotice } from '../Rules/RuleSaveConfirm';
// The five holiday-provider failures are structured errors whose localized copy
// names the source (`{{provider}}`). Reading `e.message` showed the backend's
// English sentence verbatim in all nine locales — the catalog entries added on
// 2026-08-03 only reach the screen through this helper.
import { translateApiError } from '../../../i18n/translateApiError';
import { useTranslation } from 'react-i18next';
import DateField from '../../../components/inputs/DateField';
import {
  buildHolidayImportPayload, buildManualHolidayPayload, defaultHolidaySelection,
  inferHolidayImportRegion, suggestionKey,
} from './holidayContract';

function dayOfWeekIdx(iso: string): number {
  return new Date(`${iso}T00:00:00`).getDay();
}

interface HolidayForm {
  location_id: string;
  name: string;
  date: string;
  recurring: boolean;
}

const EMPTY_FORM: HolidayForm = { location_id: '', name: '', date: '', recurring: true };
const currentYear = new Date().getFullYear();

const PublicHolidays: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language.split('-')[0].toUpperCase();
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [locationFilter, setLocationFilter] = useState('');
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PublicHoliday | null>(null);
  const [form, setForm] = useState<HolidayForm>({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [pendingDelete, setPendingDelete] = useState<PublicHoliday | null>(null);
  const [recalcNeeded, setRecalcNeeded] = useState(false);
  const [selectedHolidayIds, setSelectedHolidayIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [importOpen, setImportOpen] = useState(false);
  const [importLocationId, setImportLocationId] = useState('');
  const [importYear, setImportYear] = useState(currentYear);
  const [importCountry, setImportCountry] = useState('');
  const [importSubdivision, setImportSubdivision] = useState('');
  const [importCountries, setImportCountries] = useState<PublicHolidayImportCountry[]>([]);
  const [importSubdivisions, setImportSubdivisions] = useState<PublicHolidayImportSubdivision[]>([]);
  const [regionNeedsConfirmation, setRegionNeedsConfirmation] = useState(false);
  const [suggestions, setSuggestions] = useState<PublicHolidaySuggestion[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importSaving, setImportSaving] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importLocationError, setImportLocationError] = useState(false);
  const listGenerationRef = useRef(0);
  const importGenerationRef = useRef(0);

  const fetchAll = useCallback(async () => {
    const generation = ++listGenerationRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await attendanceApi.publicHolidaysApi.list(locationFilter || undefined);
      if (generation !== listGenerationRef.current) return;
      setHolidays([...data].sort((a, b) => a.date.localeCompare(b.date)));
      const availableIds = new Set(data.map((holiday) => holiday.id));
      setSelectedHolidayIds((current) => new Set(
        Array.from(current).filter((id) => availableIds.has(id)),
      ));
    } catch (e) {
      if (generation !== listGenerationRef.current) return;
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:publicHolidays.loadFailed'));
      setHolidays([]);
    } finally {
      if (generation === listGenerationRef.current) setLoading(false);
    }
  }, [locationFilter, t]);

  useEffect(() => {
    fetchAll();
    return () => { listGenerationRef.current += 1; };
  }, [fetchAll]);
  useEffect(() => {
    organizationApi.locationsApi.list()
      .then((rows) => setLocations(rows.filter((location) => location.active !== false)))
      .catch(() => setLocations([]));
  }, []);

  useEffect(() => () => { importGenerationRef.current += 1; }, []);

  const locationById = useMemo(
    () => new Map(locations.map((location) => [location.id, location])),
    [locations],
  );
  const filtered = useMemo(
    () => holidays.filter((holiday) => holiday.name.toLowerCase().includes(filterText.toLowerCase())),
    [holidays, filterText],
  );
  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );
  const filteredHolidayIds = useMemo(
    () => filtered.map((holiday) => holiday.id),
    [filtered],
  );
  const selectedHolidayCount = selectedHolidayIds.size;
  const allFilteredSelected = filteredHolidayIds.length > 0 &&
    filteredHolidayIds.every((id) => selectedHolidayIds.has(id));
  const someFilteredSelected = filteredHolidayIds.some((id) => selectedHolidayIds.has(id));

  const toggleHoliday = (id: string) => {
    setSelectedHolidayIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedHolidayIds((current) => {
      const next = new Set(current);
      if (allFilteredSelected) {
        filteredHolidayIds.forEach((id) => next.delete(id));
      } else {
        filteredHolidayIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const openManualDialog = (item?: PublicHoliday) => {
    setFormErrors({});
    if (item) {
      setEditing(item);
      setForm({
        location_id: item.location_id ?? '',
        name: item.name,
        date: item.date,
        recurring: item.recurring,
      });
    } else {
      setEditing(null);
      setForm({
        ...EMPTY_FORM,
        location_id: locationFilter || locations[0]?.id || '',
      });
    }
    setDialogOpen(true);
  };

  /**
   * A holiday on a date that has already passed changes how that day was SCORED — the
   * engine reads public holidays when it computes each daily summary. Nothing recomputes
   * them: `attendance_recalc_cron` revisits only each org's local yesterday and today, so
   * the report for a month that already closed keeps its old numbers until someone runs a
   * recalculation. Adding, editing or deleting such a holiday therefore has a second half
   * that the admin has to be told about.
   *
   * Future dates need no notice — they have not been scored yet.
   */
  const isAlreadyScored = (isoDate: string | null | undefined): boolean => {
    if (!isoDate) return false;
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return isoDate <= todayKey;   // ISO dates compare lexicographically
  };

  const handleSave = async () => {
    const errors: Record<string, string> = {};
    if (!form.location_id) errors.location = t('attendance:publicHolidays.locationRequired');
    if (!form.name.trim()) errors.name = t('attendance:publicHolidays.nameRequired');
    if (!form.date) errors.date = t('attendance:publicHolidays.dateRequired');
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload: PublicHolidayCreate = buildManualHolidayPayload({
      locationId: form.location_id,
      name: form.name,
      date: form.date,
      recurring: form.recurring,
    });
    setSaving(true);
    try {
      if (editing) {
        await attendanceApi.publicHolidaysApi.update(editing.id, payload);
        setToast(t('attendance:publicHolidays.toastUpdated', { name: payload.name }));
      } else {
        await attendanceApi.publicHolidaysApi.create(payload);
        setToast(t('attendance:publicHolidays.toastAdded', { name: payload.name }));
      }
      if (isAlreadyScored(payload.date as string) || (editing && isAlreadyScored(editing.date as string))) {
        // Either the new date or the date it USED to be on has already been scored.
        setRecalcNeeded(true);
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:publicHolidays.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.publicHolidaysApi.delete(pendingDelete.id);
      setToast(t('attendance:publicHolidays.toastDeleted', { name: pendingDelete.name }));
      if (isAlreadyScored(pendingDelete.date as string)) setRecalcNeeded(true);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:publicHolidays.deleteFailed'));
      throw e;
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedHolidayIds);
    if (ids.length === 0) return;

    const results = await Promise.allSettled(
      ids.map((id) => attendanceApi.publicHolidaysApi.delete(id)),
    );
    const failedIds = ids.filter((_, index) => results[index].status === 'rejected');
    const deletedCount = ids.length - failedIds.length;
    setSelectedHolidayIds(new Set(failedIds));

    if (deletedCount > 0) await fetchAll();
    if (failedIds.length === 0) {
      setToast(t('attendance:publicHolidays.toastBulkDeleted', { count: deletedCount }));
      return;
    }
    if (deletedCount > 0) {
      setToast(t('attendance:publicHolidays.toastBulkDeletedPartial', {
        deleted: deletedCount,
        failed: failedIds.length,
      }));
      return;
    }

    setToast(t('attendance:publicHolidays.bulkDeleteFailed'));
    throw new Error('Bulk public holiday deletion failed');
  };

  const loadCountryOptions = async (country: string, state?: string | null) => {
    const generation = ++importGenerationRef.current;
    if (!country) {
      setImportSubdivisions([]);
      setImportSubdivision('');
      setImportLoading(false);
      return;
    }
    setImportLoading(true);
    setImportError(null);
    try {
      const options = await attendanceApi.publicHolidaysApi.importOptions(language, country);
      if (generation !== importGenerationRef.current) return;
      setImportCountries(options.countries);
      setImportSubdivisions(options.subdivisions);
      const provider = options.countries.find((item) => item.code.toUpperCase() === country.toUpperCase())?.provider;
      if (state !== undefined) {
        const inferred = inferHolidayImportRegion(country, state, provider, options.subdivisions);
        setImportSubdivision(inferred.subdivision);
        setRegionNeedsConfirmation(inferred.needsConfirmation);
      }
    } catch (e) {
      if (generation !== importGenerationRef.current) return;
      setImportError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:publicHolidays.importSourceError'));
      setImportSubdivisions([]);
    } finally {
      if (generation === importGenerationRef.current) setImportLoading(false);
    }
  };

  const handleOpenImport = async () => {
    const generation = ++importGenerationRef.current;
    const locationId = locationFilter || locations[0]?.id || '';
    const location = locationById.get(locationId);
    const country = location?.country?.trim().toUpperCase() || '';
    setImportOpen(true);
    setImportLocationId(locationId);
    setImportLocationError(false);
    setImportYear(currentYear);
    setImportCountry(country);
    setImportSubdivision('');
    setImportSubdivisions([]);
    setRegionNeedsConfirmation(false);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setPreviewLoaded(false);
    setImportError(null);
    setImportLoading(true);
    try {
      const options = await attendanceApi.publicHolidaysApi.importOptions(language);
      if (generation !== importGenerationRef.current) return;
      setImportCountries(options.countries);
      if (country) await loadCountryOptions(country, location?.state);
    } catch (e) {
      if (generation !== importGenerationRef.current) return;
      setImportError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:publicHolidays.importSourceError'));
    } finally {
      if (generation === importGenerationRef.current) setImportLoading(false);
    }
  };

  const handleImportLocation = (locationId: string) => {
    const location = locationById.get(locationId);
    const country = location?.country?.trim().toUpperCase() || '';
    setImportLocationId(locationId);
    setImportLocationError(false);
    setImportCountry(country);
    setImportSubdivision('');
    setRegionNeedsConfirmation(false);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setPreviewLoaded(false);
    void loadCountryOptions(country, location?.state);
  };

  const handleImportCountry = (country: string) => {
    setImportCountry(country);
    setImportSubdivision('');
    setRegionNeedsConfirmation(false);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setPreviewLoaded(false);
    void loadCountryOptions(country);
  };

  const loadSuggestions = async () => {
    if (!importLocationId) {
      setImportLocationError(true);
      return;
    }
    if (!importCountry) return;
    const generation = ++importGenerationRef.current;
    setImportLoading(true);
    setImportError(null);
    try {
      const response = await attendanceApi.publicHolidaysApi.suggestions({
        location_id: importLocationId,
        country: importCountry,
        year: importYear,
        subdivision: importSubdivision || undefined,
        language,
      });
      if (generation !== importGenerationRef.current) return;
      setSuggestions(response.items);
      setSelectedSuggestions(defaultHolidaySelection(response.items));
      setPreviewLoaded(true);
    } catch (e) {
      if (generation !== importGenerationRef.current) return;
      setImportError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:publicHolidays.importSourceError'));
      setPreviewLoaded(false);
    } finally {
      if (generation === importGenerationRef.current) setImportLoading(false);
    }
  };

  const toggleSuggestion = (item: PublicHolidaySuggestion) => {
    if (item.already_exists) return;
    const key = suggestionKey(item);
    setSelectedSuggestions((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const submitImport = async () => {
    const payload = buildHolidayImportPayload({
      locationId: importLocationId,
      country: importCountry,
      subdivision: importSubdivision,
      language,
      year: importYear,
      suggestions,
      selected: selectedSuggestions,
    });
    if (payload.items.length === 0) return;
    setImportSaving(true);
    setImportError(null);
    try {
      const result = await attendanceApi.publicHolidaysApi.import(payload);
      setToast(t('attendance:publicHolidays.importResult', {
        added: result.created,
        skipped: result.skipped,
      }));
      importGenerationRef.current += 1;
      setImportOpen(false);
      await fetchAll();
    } catch (e) {
      setImportError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:publicHolidays.importSaveError'));
    } finally {
      setImportSaving(false);
    }
  };

  const closeImport = () => {
    importGenerationRef.current += 1;
    setImportLoading(false);
    setImportOpen(false);
  };

  const selectedCount = suggestions.filter(
    (item) => !item.already_exists && selectedSuggestions.has(suggestionKey(item)),
  ).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 1.5, gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('attendance:publicHolidays.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:publicHolidays.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Tooltip title={t('common:actions.refresh')}>
            <span>
              <IconButton size="small" onClick={fetchAll} disabled={loading} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {selectedHolidayCount > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlined />}
              onClick={() => setBulkDeleteOpen(true)}
              sx={{ borderRadius: 1, textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {t('attendance:publicHolidays.bulkDeleteAction', { count: selectedHolidayCount })}
            </Button>
          )}
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlined />} onClick={handleOpenImport} disabled={locations.length === 0} sx={{ borderRadius: 1, textTransform: 'none' }}>
            {t('attendance:publicHolidays.importButton')}
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openManualDialog()} disabled={locations.length === 0} sx={{ borderRadius: 1, textTransform: 'none' }}>
            {t('attendance:publicHolidays.addHoliday')}
          </Button>
        </Stack>
      </Box>

      {recalcNeeded && <RecalcNotice reason="holiday" onDismiss={() => setRecalcNeeded(false)} />}

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
      {!loading && locations.length === 0 && <Alert severity="info" sx={{ width: '100%', mb: 2 }}>{t('attendance:publicHolidays.noLocations')}</Alert>}

      <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t('attendance:publicHolidays.locationLabel')}</InputLabel>
          <Select value={locationFilter} label={t('attendance:publicHolidays.locationLabel')} onChange={(event) => { listGenerationRef.current += 1; setLocationFilter(event.target.value); setSelectedHolidayIds(new Set()); setBulkDeleteOpen(false); setPage(0); }}>
            <MenuItem value="">{t('attendance:publicHolidays.allLocations')}</MenuItem>
            {locations.map((location) => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label={t('attendance:publicHolidays.filterLabel')} value={filterText} onChange={(event) => { setFilterText(event.target.value); setSelectedHolidayIds(new Set()); setBulkDeleteOpen(false); setPage(0); }} size="small" sx={{ width: 280 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={filtered.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }} />
      </Box>

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allFilteredSelected}
                    indeterminate={!allFilteredSelected && someFilteredSelected}
                    onChange={toggleAllFiltered}
                    inputProps={{ 'aria-label': t('attendance:publicHolidays.selectAll') }}
                  />
                </TableCell>
                <TableCell>{t('attendance:publicHolidays.colHoliday')}</TableCell>
                <TableCell>{t('attendance:publicHolidays.colLocation')}</TableCell>
                <TableCell>{t('attendance:publicHolidays.colDate')}</TableCell>
                <TableCell>{t('attendance:publicHolidays.colDay')}</TableCell>
                <TableCell>{t('attendance:publicHolidays.colSource')}</TableCell>
                <TableCell align="center">{t('attendance:publicHolidays.colRecurring')}</TableCell>
                <TableCell align="center">{t('attendance:publicHolidays.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((holiday) => {
                const dayIdx = dayOfWeekIdx(holiday.date);
                return (
                  <TableRow key={holiday.id} hover selected={selectedHolidayIds.has(holiday.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedHolidayIds.has(holiday.id)}
                        onChange={() => toggleHoliday(holiday.id)}
                        inputProps={{
                          'aria-label': `${t('attendance:publicHolidays.selectHoliday')}: ${holiday.name}`,
                        }}
                      />
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{holiday.name}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" color={holiday.location_id ? 'text.primary' : 'text.secondary'}>
                        {holiday.location_name || (holiday.location_id ? locationById.get(holiday.location_id)?.name : t('attendance:publicHolidays.legacyOrganizationWide')) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{holiday.date}</Typography></TableCell>
                    <TableCell><Chip label={t(`attendance:publicHolidays.days.${dayIdx}`)} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }} /></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{t(`attendance:publicHolidays.sources.${holiday.source}`)}</Typography></TableCell>
                    <TableCell align="center">{holiday.recurring ? <Chip label={t('attendance:publicHolidays.yearly')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }} /> : <Typography variant="caption" color="text.disabled">{t('attendance:publicHolidays.oneTime')}</Typography>}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t('common:actions.edit')}><IconButton size="small" onClick={() => openManualDialog(holiday)}><EditOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Tooltip title={t('common:actions.delete')}><IconButton size="small" onClick={() => setPendingDelete(holiday)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginated.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}><Typography variant="body2" color="text.secondary">{filterText ? t('attendance:publicHolidays.noMatch') : t('attendance:publicHolidays.noConfigured')}</Typography><Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>{t('attendance:publicHolidays.emptyHint')}</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>{editing ? t('attendance:publicHolidays.editTitle') : t('attendance:publicHolidays.addTitle')}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {editing?.location_id == null && <Alert severity="info">{t('attendance:publicHolidays.legacyEditHint')}</Alert>}
            <FormControl size="small" fullWidth required error={!!formErrors.location}>
              <InputLabel>{t('attendance:publicHolidays.locationLabel')}</InputLabel>
              <Select value={form.location_id} label={t('attendance:publicHolidays.locationLabel')} onChange={(event) => { setForm((current) => ({ ...current, location_id: event.target.value })); setFormErrors((current) => ({ ...current, location: '' })); }}>
                {locations.map((location) => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)}
              </Select>
              <FormHelperText>{formErrors.location || t('attendance:publicHolidays.locationHelper')}</FormHelperText>
            </FormControl>
            <TextField required label={t('attendance:publicHolidays.nameLabel')} value={form.name} onChange={(event) => { setForm((current) => ({ ...current, name: event.target.value })); setFormErrors((current) => ({ ...current, name: '' })); }} error={!!formErrors.name} helperText={formErrors.name} size="small" fullWidth />
            <DateField required label={t('attendance:publicHolidays.dateLabel')} value={form.date} onChange={(event) => { setForm((current) => ({ ...current, date: event.target.value })); setFormErrors((current) => ({ ...current, date: '' })); }} error={!!formErrors.date} helperText={formErrors.date} size="small" fullWidth />
            <FormControlLabel control={<Switch size="small" checked={form.recurring} onChange={(event) => setForm((current) => ({ ...current, recurring: event.target.checked }))} />} label={<Typography variant="body2">{t('attendance:publicHolidays.recurring')}</Typography>} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} size="small">{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} size="small" disabled={saving}>{saving ? t('common:status.saving') : editing ? t('attendance:publicHolidays.saveChanges') : t('attendance:publicHolidays.addHoliday')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => !importSaving && closeImport()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>{t('attendance:publicHolidays.importTitle')}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('attendance:publicHolidays.importDescription')}</Typography>
          {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth required error={importLocationError}>
              <InputLabel>{t('attendance:publicHolidays.locationLabel')}</InputLabel>
              <Select value={importLocationId} label={t('attendance:publicHolidays.locationLabel')} onChange={(event) => handleImportLocation(event.target.value)}>
                {locations.map((location) => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)}
              </Select>
              {importLocationError && <FormHelperText>{t('attendance:publicHolidays.locationRequired')}</FormHelperText>}
            </FormControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField label={t('attendance:publicHolidays.yearLabel')} type="number" value={importYear} onChange={(event) => { importGenerationRef.current += 1; setImportLoading(false); setImportYear(Number(event.target.value)); setPreviewLoaded(false); }} inputProps={{ min: 2000, max: 2100 }} size="small" fullWidth />
              <FormControl size="small" fullWidth required>
                <InputLabel>{t('attendance:publicHolidays.countryLabel')}</InputLabel>
                <Select value={importCountry} label={t('attendance:publicHolidays.countryLabel')} onChange={(event) => handleImportCountry(event.target.value)}>
                  {importCountries.map((country) => <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            {importSubdivisions.length > 0 ? (
              <FormControl size="small" fullWidth>
                <InputLabel>{t('attendance:publicHolidays.regionLabel')}</InputLabel>
                <Select value={importSubdivision} label={t('attendance:publicHolidays.regionLabel')} onChange={(event) => { importGenerationRef.current += 1; setImportLoading(false); setImportSubdivision(event.target.value); setRegionNeedsConfirmation(false); setPreviewLoaded(false); }}>
                  <MenuItem value="">{t('attendance:publicHolidays.nationalOnly')}</MenuItem>
                  {importSubdivisions.map((region) => <MenuItem key={region.code} value={region.code}>{region.name} ({region.code})</MenuItem>)}
                </Select>
                <FormHelperText>{regionNeedsConfirmation ? t('attendance:publicHolidays.regionConfirm') : t('attendance:publicHolidays.regionHelper')}</FormHelperText>
              </FormControl>
            ) : (
              <TextField label={t('attendance:publicHolidays.regionCodeLabel')} value={importSubdivision} onChange={(event) => { importGenerationRef.current += 1; setImportLoading(false); setImportSubdivision(event.target.value.toUpperCase()); setPreviewLoaded(false); }} size="small" fullWidth helperText={t('attendance:publicHolidays.regionCodeHelper')} />
            )}
            {regionNeedsConfirmation && <Alert severity="warning">{t('attendance:publicHolidays.regionConfirm')}</Alert>}
            <Box><Button variant="outlined" size="small" onClick={loadSuggestions} disabled={!importLocationId || !importCountry || importLoading} sx={{ borderRadius: 1, textTransform: 'none' }}>{importLoading ? t('attendance:publicHolidays.loadingSuggestions') : t('attendance:publicHolidays.reviewSuggestions')}</Button></Box>

            {previewLoaded && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{t('attendance:publicHolidays.previewTitle', { count: suggestions.length })}</Typography>
                {suggestions.length === 0 ? <Alert severity="info">{t('attendance:publicHolidays.noSuggestions')}</Alert> : (
                  <Paper variant="outlined" sx={{ maxHeight: 280, overflow: 'auto', borderRadius: 1 }}>
                    {suggestions.map((item) => (
                      <Box key={suggestionKey(item)} sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                        <Checkbox size="small" checked={!item.already_exists && selectedSuggestions.has(suggestionKey(item))} disabled={item.already_exists} onChange={() => toggleSuggestion(item)} inputProps={{ 'aria-label': item.name }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.date} · {t(`attendance:publicHolidays.sources.${item.source}`)}</Typography>
                        </Box>
                        {item.already_exists && <Chip label={t('attendance:publicHolidays.alreadyExists')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem', borderRadius: 1 }} />}
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={closeImport} size="small" disabled={importSaving}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={submitImport} size="small" disabled={!previewLoaded || selectedCount === 0 || importSaving}>{importSaving ? t('common:status.saving') : t('attendance:publicHolidays.importSelected', { count: selectedCount })}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={bulkDeleteOpen}
        tone="danger"
        title={t('attendance:publicHolidays.bulkDeleteTitle', { count: selectedHolidayCount })}
        description={t('attendance:publicHolidays.bulkDeleteDescription')}
        subject={(
          <Box sx={{ fontWeight: 600 }}>
            {t('attendance:publicHolidays.bulkDeleteSubject', { count: selectedHolidayCount })}
          </Box>
        )}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmBulkDelete}
        onClose={() => setBulkDeleteOpen(false)}
      />
      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:publicHolidays.deleteTitle')}
        description={t('attendance:publicHolidays.deleteDescription')}
        subject={pendingDelete && <Box sx={{ fontWeight: 600 }}>{pendingDelete.name} · {pendingDelete.date}</Box>}
        details={pendingDelete && isAlreadyScored(pendingDelete.date as string)
          ? t('attendance:publicHolidays.deletePastNeedsRecalc')
          : undefined}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}><Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert></Snackbar>
    </Box>
  );
};

export default PublicHolidays;
